import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

interface IProps {
	initialProgress: number;
	onChange?: (progress: number) => void;
}

interface ITrackProps {
	progress: number;
}

interface IThumbProps {
	left: () => string;
}

const Wrapper = styled.div`
	position: relative;
`;

const AdjustableProgressBarTrack = styled.input<ITrackProps>`
	appearance: none;
	cursor: pointer;
	width: 100%;
	outline: none;
	height: 10px;
	border-radius: 17px;
	background: linear-gradient(
		90deg,
		${(props) => props.theme.colors.primary} ${(props) => props.progress * 100}%,
		rgb(194, 202, 210) ${(props) => props.progress * 100}%
	);

	&:focus {
		/* Have to split the browser specific styling or it breaks, see: https://github.com/w3c/csswg-drafts/issues/4410 */
		&::-ms-thumb {
			box-shadow: 0 1px 5px ${({ theme }) => theme.colors.primary};
		}

		&::-moz-range-thumb {
			box-shadow: 0 1px 5px ${({ theme }) => theme.colors.primary};
		}

		&::-webkit-slider-thumb {
			box-shadow: 0 1px 5px ${({ theme }) => theme.colors.primary};
		}
	}

	/* Have to split the browser specific styling or it breaks, see: https://github.com/w3c/csswg-drafts/issues/4410 */
	&::-ms-thumb {
		appearance: none;
		width: 30px;
		height: 30px;
		border-radius: 50%;
		background: ${({ theme }) => theme.colors.lightBlueLight};
		box-shadow: 0 1px 3px ${({ theme }) => theme.colors.primary};
		transition: box-shadow 0.25s;
	}

	&::-moz-range-thumb {
		appearance: none;
		width: 30px;
		height: 30px;
		border-radius: 50%;
		background: ${({ theme }) => theme.colors.lightBlueLight};
		box-shadow: 0 1px 3px ${({ theme }) => theme.colors.primary};
		transition: box-shadow 0.25s;
	}

	&::-webkit-slider-thumb {
		appearance: none;
		width: 30px;
		height: 30px;
		border-radius: 50%;
		background: ${({ theme }) => theme.colors.lightBlueLight};
		box-shadow: 0 1px 3px ${({ theme }) => theme.colors.primary};
		transition: box-shadow 0.25s;
	}
`;

const ThumbBurger = styled.div<IThumbProps>`
	position: absolute;
	left: ${({ left }) => left};
	top: 0;
	transform: translateY(-6px);
	height: 30px;
	width: 14px;
	pointer-events: none;

	span {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		height: 2px;
		width: 14px;
		background-color: ${({ theme }) => theme.colors.lightBlueDark};
		border-radius: 1px;

		&:first-child {
			top: 10px;
		}

		&:last-child {
			bottom: 10px;
		}
	}
`;

const AdjustableProgressBar = ({ initialProgress = 0, onChange }: IProps) => {
	const calculatedProgress = initialProgress;
	const [progress, setProgress] = useState(calculatedProgress);

	useEffect(() => {
		if (onChange) {
			onChange(progress);
		}
	}, [progress]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
		setProgress(Number(event.target.value));
	const calculateLeftPosition = () => `calc(${progress * 100}% + (${8 - progress * 30}px))`;

	return (
		<Wrapper>
			<AdjustableProgressBarTrack
				type="range"
				min={0}
				max={1}
				step={0.05}
				progress={progress}
				defaultValue={progress}
				onChange={handleChange}
			/>
			<ThumbBurger left={calculateLeftPosition}>
				<span />
				<span />
			</ThumbBurger>
		</Wrapper>
	);
};

export default AdjustableProgressBar;
